import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { HourglassIcon, LogOutIcon, RefreshCwIcon } from "lucide-react";
import { hasAccess } from "@scandi/shared";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { ScandiWordmark } from "@/components/brand/scandi-wordmark";
import { LanguageToggle } from "@/components/layout/language-toggle";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { useLogout, useSession } from "@/hooks/use-session";
import { initials } from "@/features/auth/initials";

/**
 * The waiting room: signed in, but no admin has given this account a role.
 *
 * Rendered in place of the whole app rather than as a route, so there is no
 * page behind it to load and fail. It says who is signed in, because the most
 * common way to end up here by mistake is picking the wrong Google account, and
 * it offers a way to check again without a reload once someone says "done".
 */
export function NoAccessPage() {
  const { t } = useTranslation();
  const { user, refetch, isRefetching } = useSession();
  const logout = useLogout();

  async function checkAgain() {
    const { data, isError } = await refetch();
    // A failed re-check keeps the old cached answer in `data`, so it has to be
    // told apart first: saying "still waiting" would claim a check that never
    // happened, while the admin may already have granted access.
    if (isError) {
      toast.error(t("noAccess.checkFailed"));
      return;
    }
    // When access has arrived the app swaps screens by itself; only the
    // unchanged answer needs saying, or the button looks like it did nothing.
    if (data?.authenticated && !hasAccess(data.role)) toast.info(t("noAccess.stillWaiting"));
  }

  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden p-4">
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="brand-gradient absolute -top-40 left-1/2 size-[36rem] -translate-x-1/2 rounded-full opacity-10 blur-3xl" />
      </div>

      <div className="absolute top-4 right-4 flex items-center gap-1">
        <LanguageToggle />
        <ThemeToggle />
      </div>

      <div className="flex w-full max-w-md flex-col gap-8">
        <div className="flex justify-center">
          <ScandiWordmark className="h-7" gradient />
        </div>

        <Card className="shadow-lg shadow-black/5">
          <CardContent className="flex flex-col items-center gap-5 text-center">
            <div className="bg-warning-muted text-warning flex size-12 items-center justify-center rounded-full">
              <HourglassIcon className="size-6" />
            </div>

            <div className="flex flex-col gap-2">
              <h1 className="text-xl font-bold tracking-tight text-balance">{t("noAccess.title")}</h1>
              <p className="text-muted-foreground text-sm text-balance">{t("noAccess.description")}</p>
            </div>

            {user && (
              <div className="bg-muted/50 flex w-full min-w-0 items-center gap-3 rounded-lg border p-3 text-left">
                <Avatar size="lg">
                  {user.photoUrl && (
                    <AvatarImage src={user.photoUrl} alt="" referrerPolicy="no-referrer" />
                  )}
                  <AvatarFallback>{initials(user.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{user.name}</p>
                  <p className="text-muted-foreground truncate text-xs">{user.email}</p>
                </div>
              </div>
            )}

            <p className="text-sm text-balance">{t("noAccess.contactAdmin")}</p>
          </CardContent>

          <CardFooter className="flex-col gap-3">
            <div className="flex w-full flex-col gap-2 sm:flex-row">
              <Button className="sm:flex-1" disabled={isRefetching} onClick={() => void checkAgain()}>
                {isRefetching ? <Spinner data-icon="inline-start" /> : <RefreshCwIcon data-icon="inline-start" />}
                {isRefetching ? t("noAccess.checking") : t("noAccess.checkAgain")}
              </Button>
              <Button
                className="sm:flex-1"
                variant="outline"
                disabled={logout.isPending}
                onClick={() => logout.mutate()}
              >
                {logout.isPending ? <Spinner data-icon="inline-start" /> : <LogOutIcon data-icon="inline-start" />}
                {t("noAccess.signOut")}
              </Button>
            </div>
            <p className="text-muted-foreground text-xs text-balance">{t("noAccess.wrongAccount")}</p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
